import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Package,
  Layers,
  Thermometer,
  CheckCircle,
  AlertCircle,
  Clock,
  ArrowLeft,
  Printer,
  ExternalLink,
  Snowflake,
  Factory,
  Paintbrush,
  Scissors,
  Box,
} from "lucide-react";
import type { LabelType } from "@/types/labels";

interface LabelPrint {
  id: string;
  short_code: string;
  label_type: LabelType;
  plotter_cut_id?: string;
  plybook_execution_id?: string;
  work_order_id?: string;
  data_snapshot: Record<string, unknown>;
  printed_at: string;
  is_active: boolean;
}

export default function TraceabilityView() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
   
  const [label, setLabel] = useState<LabelPrint | null>(null);
  const [loading, setLoading] = useState(true);
  const [traceData, setTraceData] = useState<any>(null);

  useEffect(() => {
    if (!code) return;

    const fetchTraceability = async () => {
      setLoading(true);
      try {
        // Find the label by short code
        const { data: labelData, error } = await api
          .from("label_prints")
          .select("*")
          .eq("short_code", code)
          .single();

        if (error || !labelData) {
          // Try legacy format (PKG-XXXX)
          const { data: plotterCut } = await api
            .from("plotter_cuts")
            .select("*")
            .eq("package_code", code)
            .single();

          if (plotterCut) {
            setTraceData({
              type: "plotter",
              ...plotterCut,
            });
          }
          setLoading(false);
          return;
        }

        setLabel(labelData);

        // Fetch full data based on label type
        if (labelData.label_type === "plotter" && labelData.plotter_cut_id) {
          const { data } = await api
            .from("plotter_cuts")
            .select(`
              *,
              created_by:profiles(full_name)
            `)
            .eq("id", labelData.plotter_cut_id)
            .single();
          setTraceData({ type: "plotter", ...data });
        } else if (labelData.label_type === "lamination" && labelData.plybook_execution_id) {
          const { data } = await api
            .from("plybook_executions")
            .select(`
              *,
              operator:profiles(full_name),
              plybook:plybooks(code, name),
              work_order:work_orders(
                order_number,
                product_model:product_models(name),
                mold:molds(code)
              )
            `)
            .eq("id", labelData.plybook_execution_id)
            .single();
          setTraceData({ type: "lamination", ...data });
        } else if (labelData.label_type === "final" && labelData.work_order_id) {
          // Full traceability - similar to FinalTraceabilityLabel fetch
          const { data: wo } = await api
            .from("work_orders")
            .select(`
              *,
              product_model:product_models(name),
              customer:billing_customers(legal_name),
              mold:molds(code)
            `)
            .eq("id", labelData.work_order_id)
            .single();

          const { data: processes } = await api
            .from("process_records")
            .select(`
              *,
              process:manufacturing_processes(code, name),
              operator:profiles(full_name)
            `)
            .eq("work_order_id", labelData.work_order_id)
            .order("started_at", { ascending: true });

          setTraceData({
            type: "final",
            workOrder: wo,
            processes: processes || [],
          });
        }
      } catch (err) {
        console.error("Error fetching traceability:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTraceability();
  }, [code]);

  if (loading) {
    return (
      <div className="container mx-auto p-4 max-w-2xl">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!traceData) {
    return (
      <div className="container mx-auto p-4 max-w-2xl">
        <Card>
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
            <h2 className="text-xl font-semibold mb-2">Codigo no encontrado</h2>
            <p className="text-muted-foreground mb-4">
              El codigo <code className="bg-muted px-2 py-1 rounded">{code}</code> no existe en el sistema.
            </p>
            <Button onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
        <Badge variant="outline" className="font-mono">
          {code}
        </Badge>
      </div>

      {/* Plotter Cut View */}
      {traceData.type === "plotter" && (
        <Card>
          <CardHeader className="bg-blue-50 dark:bg-blue-950/30">
            <div className="flex items-center gap-2">
              <Package className="w-6 h-6 text-blue-600" />
              <div>
                <CardTitle>{traceData.package_code}</CardTitle>
                <CardDescription>{traceData.package_name}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            {/* Status */}
            <div className="flex items-center gap-2">
              <Badge
                variant={traceData.status === "available" ? "default" : "secondary"}
              >
                {traceData.status === "available" && "Disponible"}
                {traceData.status === "reserved" && "Reservado"}
                {traceData.status === "in_use" && "En uso"}
                {traceData.status === "consumed" && "Consumido"}
              </Badge>
              {traceData.status === "available" && (
                <Badge variant="outline" className="bg-cyan-50 text-cyan-700">
                  <Snowflake className="w-3 h-3 mr-1" />
                  En congelador
                </Badge>
              )}
            </div>

            <Separator />

            {/* Materials */}
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Materiales
              </h4>
              <div className="space-y-1 text-sm">
                {(traceData.materials || []).map((m: any, i: number) => (
                  <div key={i} className="flex justify-between">
                    <span>{m.material_name}</span>
                    <span className="text-muted-foreground">
                      {m.layers} capas {m.gsm && `(${m.gsm} gsm)`}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Details */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Lote:</span>
                <span className="ml-2 font-medium">{traceData.batch_number || "N/A"}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Fecha corte:</span>
                <span className="ml-2">
                  {traceData.cut_date
                    ? format(new Date(traceData.cut_date), "dd/MM/yyyy", { locale: es })
                    : "N/A"}
                </span>
              </div>
              {traceData.weight_grams && (
                <div>
                  <span className="text-muted-foreground">Peso:</span>
                  <span className="ml-2">{traceData.weight_grams}g</span>
                </div>
              )}
              {traceData.dimensions && (
                <div>
                  <span className="text-muted-foreground">Dimensiones:</span>
                  <span className="ml-2">{traceData.dimensions}</span>
                </div>
              )}
            </div>

            {/* Freezer warning */}
            {traceData.out_time_limit_minutes && (
              <div className="bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <div>
                  <span className="font-medium">Tiempo maximo fuera del congelador:</span>
                  <span className="ml-2">
                    {Math.floor(traceData.out_time_limit_minutes / 60)}h{" "}
                    {traceData.out_time_limit_minutes % 60}m
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lamination View */}
      {traceData.type === "lamination" && (
        <Card>
          <CardHeader className="bg-green-50 dark:bg-green-950/30">
            <div className="flex items-center gap-2">
              <Layers className="w-6 h-6 text-green-600" />
              <div>
                <CardTitle>{traceData.work_order?.order_number}</CardTitle>
                <CardDescription>
                  {traceData.work_order?.product_model?.name} - Molde {traceData.work_order?.mold?.code}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            {/* Plybook info */}
            <div className="flex items-center justify-between">
              <div>
                <span className="text-muted-foreground">Plybook:</span>
                <span className="ml-2 font-medium">{traceData.plybook?.code}</span>
              </div>
              <Badge variant={traceData.status === "completed" ? "default" : "secondary"}>
                {traceData.status === "completed" && <CheckCircle className="w-3 h-3 mr-1" />}
                {traceData.status}
              </Badge>
            </div>

            {/* Progress */}
            <div className="bg-muted p-3 rounded-lg">
              <div className="flex justify-between mb-1">
                <span>Capas confirmadas</span>
                <span className="font-bold">
                  {traceData.confirmed_plies} / {traceData.total_plies}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full"
                  style={{
                    width: `${(traceData.confirmed_plies / traceData.total_plies) * 100}%`,
                  }}
                />
              </div>
            </div>

            {/* Timing */}
            {traceData.started_at && (
              <div className="text-sm text-muted-foreground">
                <div>Inicio: {format(new Date(traceData.started_at), "dd/MM/yy HH:mm", { locale: es })}</div>
                {traceData.completed_at && (
                  <div>Fin: {format(new Date(traceData.completed_at), "dd/MM/yy HH:mm", { locale: es })}</div>
                )}
              </div>
            )}

            {/* Operator */}
            <div className="text-sm">
              <span className="text-muted-foreground">Operador:</span>
              <span className="ml-2">{traceData.operator?.full_name || "N/A"}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Final Traceability View */}
      {traceData.type === "final" && (
        <Card>
          <CardHeader className="bg-purple-50 dark:bg-purple-950/30">
            <div className="flex items-center gap-2">
              <Box className="w-6 h-6 text-purple-600" />
              <div>
                <CardTitle>{traceData.workOrder?.order_number}</CardTitle>
                <CardDescription>
                  {traceData.workOrder?.product_model?.name} - {traceData.workOrder?.customer?.legal_name}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <h4 className="font-medium">Cadena de Trazabilidad</h4>

            {/* Process timeline */}
            <div className="space-y-2">
              {(traceData.processes || []).map((proc: any, i: number) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-2 rounded bg-muted/50"
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      proc.status === "completed"
                        ? "bg-green-100 text-green-600"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {proc.process?.code === "LAMI" && <Layers className="w-4 h-4" />}
                    {proc.process?.code === "CURA" && <Thermometer className="w-4 h-4" />}
                    {proc.process?.code === "CORT" && <Scissors className="w-4 h-4" />}
                    {proc.process?.code === "PIN" && <Paintbrush className="w-4 h-4" />}
                    {!["LAMI", "CURA", "CORT", "PIN"].includes(proc.process?.code) && (
                      <Factory className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{proc.process?.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {proc.operator?.full_name}
                      {proc.completed_at && (
                        <> - {format(new Date(proc.completed_at), "dd/MM HH:mm", { locale: es })}</>
                      )}
                    </div>
                  </div>
                  {proc.status === "completed" && (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                </div>
              ))}
            </div>

            {/* Work order link */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate(`/work-orders/${traceData.workOrder?.id}`)}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Ver Orden de Trabajo Completa
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Print date */}
      {label && (
        <div className="mt-4 text-center text-sm text-muted-foreground">
          Etiqueta impresa: {format(new Date(label.printed_at), "dd/MM/yyyy HH:mm", { locale: es })}
        </div>
      )}
    </div>
  );
}
